import React from 'react';
import { useAuthContext } from '@asgardeo/auth-react';
import stopwords from '../../../utils/stopwords.json';
import './Hit.css';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  encodeCaseNumberForPath,
  normalizeCaseNumbers,
} from '../../../utils/caseNumber';

const PRESIGNED_BASE_URL = import.meta.env.VITE_PRESIGNED_BASE_URL;

const Hit = ({ hit, showScore = false }) => {
  const navigate = useNavigate();
  const { state, signIn, getAccessToken } = useAuthContext();
  const currentPage = useSelector((state) => state.home.currentPage);
  const groupedHits =
    Array.isArray(hit._grouped_hits) && hit._grouped_hits.length > 0
      ? hit._grouped_hits
      : [hit];
  const legislation = hit.legislation || '';

  const selectedOption = useSelector((state) => state.home.selectedOption);
  const searchType = selectedOption;
  const standardCaseNumbers = normalizeCaseNumbers(hit.standard_casenumber);
  const primaryCaseNumber = standardCaseNumbers[0] || '';
  const standardCaseNumberText = standardCaseNumbers.join(', ');
  const primaryCaseNumberLower = primaryCaseNumber.toLowerCase();
  const summaryPath = primaryCaseNumber
    ? `/summary/${encodeCaseNumberForPath(primaryCaseNumber)}`
    : null;
  window.dataLayer = window.dataLayer || [];

  const handleLinkClick = async (event) => {
    event.preventDefault();

    if (!hit.link) return;

    if (!state.isAuthenticated) {
      sessionStorage.setItem('redirectToDocument', hit.link);
      window.dataLayer.push({
        event: 'login prompt',
        event_category: 'User',
        event_label: 'Login Redirect',
        value: 'redirectToDocument',
      });

      const urlString = window.location.search;
      const currentPath = window.location.pathname;

      if (currentPath === '/search') {
        sessionStorage.removeItem('redirectAfterLogin');
        if (urlString) {
          sessionStorage.setItem('urlString', urlString);
        } else {
          sessionStorage.removeItem('urlString');
        }
      } else {
        sessionStorage.setItem('redirectAfterLogin', currentPath);
      }

      signIn();
      window.dataLayer.push({
        event: 'login',
        event_category: 'User',
        event_label: 'Successful Login',
        value: 'urlString',
      });
      return;
    }

    const newWindow = window.open('about:blank', '_blank');

    try {
      let accessToken = '';
      try {
        accessToken = (await getAccessToken()) || '';
      } catch (tokenError) {
        console.error('Failed to get access token for PDF:', tokenError);
      }

      const response = await fetch(`${PRESIGNED_BASE_URL}/api/pdf/get-pdf-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ link: hit.link }),
      });
      const data = await response.json();

      if (data.success && newWindow) {
        newWindow.location.href = data.presigned_url;
      } else if (newWindow) {
        newWindow.location.href = hit.link;
      }
    } catch (error) {
      console.error('Error fetching pre-signed URL:', error);
      if (newWindow) {
        newWindow.location.href = hit.link;
      }
    }
  };

  const handleViewSummary = () => {
    if (!summaryPath) {
      return;
    }

    sessionStorage.setItem(
      'summaryBackPath',
      JSON.stringify({
        summaryPath,
        backPath: `${window.location.pathname}${window.location.search}`,
      })
    );

    if (!state.isAuthenticated) {
      sessionStorage.removeItem('redirectToDocument');
      sessionStorage.removeItem('urlString');
      sessionStorage.setItem('redirectAfterLogin', summaryPath);

      window.dataLayer.push({
        event: 'login prompt',
        event_category: 'User',
        event_label: 'Login Redirect',
        value: 'redirectToSummary',
      });

      signIn();
      return;
    }

    navigate(summaryPath);
  };

  const formatDate = (date) => {
    const d = new Date(date);
    if (isNaN(d)) {
      console.error('Invalid date:', date);
      return '';
    }
    const day = d.getDate();
    const month = d.toLocaleString('default', { month: 'short' });
    const year = d.getFullYear();
    const suffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    };

    return `${day}${suffix(day)} ${month} ${year}`;
  };

  const date = hit.date ? formatDate(hit.date) : '';

  const judges = hit.judge_final || [];
  const chunkPreviews = groupedHits
    .map((groupedHit) => getChunkPreview(groupedHit, searchType, stopwords))
    .filter(Boolean);
  const matchingChunkCount = hit._group_found || chunkPreviews.length;
  const visibleChunkPreviews = chunkPreviews.slice(0, 2);

  const judgeText =
    judges.length > 0 && !(judges.length === 1 && judges[0] === 'NA') ? (
      <div className="mt-1 text-14 text-pl_txt_light_1 dark:text-pl_txt_dark_1">
        <span className="font-medium">
          {judges.length > 1 ? 'Judges:' : 'Judge:'}
        </span>{' '}
        {judges.join(', ')}
      </div>
    ) : null;

  const partiesText =
    hit.nameofparties && hit.nameofparties.trim() !== '' ? (
      <div className="min-w-0 flex-1 md:mt-1">
        <span className="font-medium text-14 text-pl_txt_light_1 dark:text-pl_txt_dark_1">
          {primaryCaseNumberLower.startsWith('sd')
            ? 'Special determination on:'
            : standardCaseNumberText.toLowerCase().includes('digest')
            ? 'Digest:'
            : 'Parties:'}
        </span>{' '}
        <span className="text-14 text-pl_txt_light_1 dark:text-pl_txt_dark_1">
          {primaryCaseNumberLower.startsWith('sd')
            ? hit.nameofparties
            : displayNameOfParties(hit.nameofparties)}
        </span>
      </div>
    ) : null;

  const shouldShowScore = showScore;

  const scoreElement = shouldShowScore && hit._relevanceScore ? (
    <span
      className="dark:text-pl_txt_dark_3 text-pl_txt_light_3 text-12"
      title={`Search Score: ${hit._relevanceScore.score}\nScore source: ${hit._relevanceScore.scoreSource}\nMatching chunks: ${hit._relevanceScore.chunkCount}\nScored chunks: ${hit._relevanceScore.scoredChunkCount}\nRange: ${hit._relevanceScore.minScore.toFixed(4)}-${hit._relevanceScore.maxScore.toFixed(4)}`}
    >
      Score: {hit._relevanceScore.score}
    </span>
  ) : null;

  const summaryButton = summaryPath ? (
    <button
      type="button"
      onClick={handleViewSummary}
      className="text-left text-13 leading-4 text-pl_txt_light_3 transition-colors hover:text-pl_prim_color_1 dark:text-pl_txt_dark_3 md:text-right"
    >
      View summary &rarr;
    </button>
  ) : null;

  return (
    <div className="flex flex-col px-6 pt-8">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <a
            href={hit.link}
            className="text-16 font-semibold text-pl_prim_color_1 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleLinkClick}
          >
            {standardCaseNumberText}
          </a>
          <span className="dark:text-pl_txt_dark_3 text-pl_txt_light_3 text-12">
            {date}
          </span>
        </div>
        <div className="hidden md:block">{summaryButton}</div>
      </div>

      {(scoreElement || summaryButton) && (
        <div className="mt-1 flex items-center justify-between gap-3 md:hidden">
          <div>{scoreElement}</div>
          <div>{summaryButton}</div>
        </div>
      )}

      {(partiesText || scoreElement) && (
        <div className="md:flex md:items-start md:justify-between md:gap-4">
          {partiesText}
          {scoreElement && (
            <div className="hidden flex-none pt-1 md:block">{scoreElement}</div>
          )}
        </div>
      )}

      {judgeText}
      {legislation && (
        <div className="mt-1 text-pl_txt_light_2 dark:text-pl_txt_dark_3 text-14">
          <span className="font-semibold">Legislation:</span>{' '}
          <span>{legislation}</span>
        </div>
      )}
      {currentPage !== 'benchmark' && visibleChunkPreviews.length > 0 && (
        <div className="mt-1 space-y-2">
          {matchingChunkCount > 1 && (
            <div className="text-12 text-pl_txt_light_3 dark:text-pl_txt_dark_3">
              {matchingChunkCount} matching chunks in this case
              {matchingChunkCount > 2 ? ' (showing top 2)' : ''}
            </div>
          )}
          {visibleChunkPreviews.map((preview, index) => (
            <div
              key={`${hit.objectID || hit.primary_key || 'chunk'}-${index}`}
              className="highlight-text text-14 text-pl_txt_light_2 dark:text-pl_txt_dark_3"
              dangerouslySetInnerHTML={{ __html: preview }}
            ></div>
          ))}
        </div>
      )}
    </div>
  );
};

function getChunkPreview(groupedHit, searchType, stopwords) {
  const snippetText = groupedHit._snippetResult?.chunk_text?.value || '';
  const highlightText = groupedHit._highlightResult?.chunk_text?.value || '';
  const fallbackText = groupedHit.chunk_text || '';
  const text =
    searchType === 'Exact Match'
      ? snippetText || highlightText || fallbackText
      : highlightText || snippetText || fallbackText;

  return reformatMarkSentences(text, stopwords) || '';
}

function displayNameOfParties(nameOfParties) {
  let textToDisplay = '';

  if (nameOfParties && nameOfParties.length > 0) {
    if (nameOfParties.length <= 250) {
      return nameOfParties;
    }

    const vsIndex = nameOfParties.search(/vs./i);

    if (vsIndex > 0) {
      const beforeVs = nameOfParties.substring(0, Math.min(vsIndex, 50)).trim();
      const afterVs = nameOfParties.substring(vsIndex + 3, vsIndex + 53).trim();
      textToDisplay = `${beforeVs} . . . Vs. ${afterVs} . . .`;
    } else {
      textToDisplay = nameOfParties.substring(0, 100);
    }
  }

  if (textToDisplay.match(/^[^a-zA-Z]/)) {
    textToDisplay = textToDisplay.replace(/^[^a-zA-Z]+/, '');
  }

  return textToDisplay;
}

function normalizeHighlightTags(text) {
  const openMarkToken = '__OPEN_MARK__';
  const closeMarkToken = '__CLOSE_MARK__';

  return String(text || '')
    .replace(/__ais-highlight__/g, '<mark>')
    .replace(/__\/ais-highlight__/g, '</mark>')
    .replace(/<mark>/g, openMarkToken)
    .replace(/<\/mark>/g, closeMarkToken)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(new RegExp(openMarkToken, 'g'), '<mark>')
    .replace(new RegExp(closeMarkToken, 'g'), '</mark>');
}

function truncateHighlightedText(text, maxLength = 250) {
  if (text.length <= maxLength) return text;

  let truncatedText = text.substring(0, maxLength);
  const lastTagStart = truncatedText.lastIndexOf('<');
  const lastTagEnd = truncatedText.lastIndexOf('>');

  if (lastTagStart > lastTagEnd) {
    truncatedText = truncatedText.substring(0, lastTagStart);
  }

  const openMarkCount = (truncatedText.match(/<mark>/g) || []).length;
  const closeMarkCount = (truncatedText.match(/<\/mark>/g) || []).length;

  if (openMarkCount > closeMarkCount) {
    truncatedText += '</mark>';
  }

  return `${truncatedText}...`;
}

function reformatMarkSentences(text, stopwords) {
  const normalizedText = normalizeHighlightTags(text);
  let sentencesWithMark = getSentencesWithMark(normalizedText);

  if (sentencesWithMark.length === 0) {
    return truncateHighlightedText(normalizedText);
  }

  let reformattedSentences = sentencesWithMark.map((sentence) =>
    removeMarkIfStopword(sentence, stopwords)
  );

  let sentenceMarkCount = reformattedSentences.map((sentence) => {
    let markMatches = sentence.match(/<mark>(.*?)<\/mark>/g) || [];
    let uniqueWords = new Set(
      markMatches.map((match) =>
        match.replace('<mark>', '').replace('</mark>', '').toLowerCase()
      )
    );

    return { sentence, markCount: uniqueWords.size };
  });

  sentenceMarkCount.sort((a, b) => b.markCount - a.markCount);
  let reformatted = sentenceMarkCount.slice(0, 5).map((item) => item.sentence);

  let result = getSentencesWithMark(reformatted.join('. ')).join(' . . .');
  
  return truncateHighlightedText(result);
}

function getSentencesWithMark(text) {
  let sentences = text.split('.');
  let sentencesWithMark = sentences.filter((sentence) =>
    sentence.includes('<mark>')
  );
  return sentencesWithMark;
}

function removeMarkIfStopword(markSentence, stopwords) {
  let markMatches = markSentence.match(/<mark>(.*?)<\/mark>/g);
  if (!markMatches) {
    return markSentence;
  }

  let markWords = markMatches.map((word) =>
    word.replace('<mark>', '').replace('</mark>', '')
  );

  for (let word of markWords) {
    if (stopwords.includes(word.trim().toLowerCase())) {
      markSentence = markSentence.replace(`<mark>${word}</mark>`, word);
    }
  }
  return markSentence;
}

export default Hit;
